'use strict';
import { BaseService } from '@narrate/base-sdk';

const HEADER_ENDPOINT_OPTIONS = 'nr-endpoint-opts';
const HeaderOptionIncludeAllPaymentTerms = 'include-all-payment-terms';

/**
 * uses attributes api to store and retrieve the user accessBlock
 *
 * future work:
 *  may handle conflict resolution
 *
 * @example
 *    // using CoreService (recommended)
 *    import Core from '@narrate/core-sdk';
 *
 *    const { value: accessBlock } = await Core.api.accessBlock.get();
 *    console.log(accessBlock); // should be something like { birthdate: }
 *
 *    await Core.api.accessBlock.save({ videos: [] });
 */
class AccessBlockService extends BaseService {

  constructor() {
    super({ configPath: 'services.accessBlock' });
  }

  async find(id) {
    return await this.requestResource('get', {
      params: {
        id
      }
    });
  }

  async findByUserId(userId) {
    console.log('revision 2: from the SDK, finding posts by UserId = ', userId);
    const { Items, ...rest } = await this.requestResource('getByUser', {
      params: { userId },
      tokenType: 'none',
    });

    for (let item of Items) {
      item.id = item.itemId.replace(/^access:/, '');
    }

    return {
      ...rest,
      Items,
    };
  }

  async save(accessBlock, id) {
    return await this.requestResource('save', {
      method: (id ? 'put' : 'post'),
      data: accessBlock,
      params: { id },
    });
  }

  async destroy(id) {
    return await this.requestResource('save', {
      method: 'delete',
      params: { id },
    });
  }

  async list() {
    const { Items, ...rest } = await this.requestRootResource({
      headers: {
        [HEADER_ENDPOINT_OPTIONS]: HeaderOptionIncludeAllPaymentTerms
      }
    });

    // TODO consider putting this on the server side
    for (let item of Items) {
      item.id = item.itemId.replace(/^access:/, '');
    }

    return {
      ...rest,
      Items
    };

  }

  /**
   * Client calls the payment intent purchase process. 
   * For returning customers, the payment intent will select a prior saved payment method.
   * When this is the case, this function will return the paymentInfo it's response which may be
   * used to let the customer know on the UI the payment type.
   * 
   * If the user overrides the payment method by picking a new card from their saved list, or entering a new card 
   * The client may call this payment intent to request a new payment intent. If this the case the client 
   * should supply the data as described in the data parameter
   * 
   * @param {string} id the id of the access block
   * @param {string} userId the owner of the access block
   * @param {object} data Optional data object with the following keys:
   *   -- action: update-payment
   *   -- txId: narrate transaction id
   *   -- client_secret: payment intent id
   *   -- paymentMethodId: (optional) in the case the customer selects a payment id from a list of saved ids
   * 
   * @returns On Success the service will return:
   * - client_secret: stripe secret key to pass to the stripe bridge commit the payment
   * - txId: the narrate transaction id to be used to confirm the payment.
   * - customerId: stripe customer id
   * - paymentInfo: payment method projection: customerId, card : { brand, exp_month, exp_year, last 4 }
   
   */
  async purchase({ userId, id }, data = {}) {
    return await this.requestResource('purchase', {
      method: 'post',
      params: { userId, id },
      data
    });
  }

  /**
   * Called when the user confirm payment. The caller must pass in the transaction Id that was sent to it when 
   * It started the payment process (see AccessBlockService.purchase() above)
   * @param txId narrate purchase transactionId
   */
  async paymentConfirmed({ txId }) {
    return await this.requestResource('paymentConfirmed', {
      method: 'post',
      params: { txId },
    });
  }
}

export default AccessBlockService;
